import { useCallback, useMemo } from 'react';

type UseSessionStorageArgs = {
  primaryKey?: string;
};

type UseSessionStorageResult = {
  write: (value: any, key?: string) => void;
  read: (key?: string) => any;
  clear: (key?: string) => void;
  getKeys: () => string[];
};

const useSessionStorage = ({ primaryKey }: UseSessionStorageArgs): UseSessionStorageResult => {
  const clear = useCallback(
    (key?: string): void => {
      if (primaryKey || key) {
        sessionStorage.removeItem(primaryKey || key);
      }
    },
    [primaryKey]
  );

  const getKeys = useCallback((): string[] => {
    return Object.keys(sessionStorage);
  }, []);

  const write = useCallback(
    (value: any, key?: string): void => {
      if (primaryKey || key) {
        sessionStorage.setItem(primaryKey || key, JSON.stringify(value));
      }
    },
    [primaryKey]
  );

  const read = useCallback(
    (key?: string): any => {
      try {
        if (primaryKey || key) {
          return JSON.parse(sessionStorage.getItem(primaryKey || key));
        }
        return undefined;
      } catch (error) {
        clear(primaryKey || key);
        return undefined;
      }
    },
    [primaryKey]
  );

  return useMemo(
    () => ({
      write,
      read,
      clear,
      getKeys,
    }),
    [write, read, clear, getKeys]
  );
};

export default useSessionStorage;
